import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery } from '@apollo/client';
import loadable from '@loadable/component';

import { Button, FlexBox, MetaTags, SvgComponent } from '@quikr/ui';
import { VIPTable } from '@quikr/ui/src/JobsApply';
import storage from '@quikr/ui/src/helpers/Storage';
import { getSessionStorage } from '@quikr/ui/src/helpers/common';
import { useStoreUtmParams } from '@quikr/ui/src/hooks';

import config from 'config';

import { common } from '../../gql/common';
import { getLink, getVipPacks } from '../../gql/vip';
import useGa from '../../hooks/ga';
import AccountIcon from './assets/AccountIcon';
import vipBenefitsSprite from './assets/vip-icons-sprite.svg';

const { IMAGE_CDN_DOMAIN } = config;

const Snackbar = loadable(() => import('@quikr/ui/src/common/Snackbar'), {
  ssr: false
});

const LoginModal = loadable(() => import('../../components/instantHire/LoginModal'), {
  ssr: false
});

const Wrapper = styled.main`
  background: ${({ theme }) => theme.colors.Neutral[300]};
  padding: 0 16px 32px;
  .mb24 {
    margin-bottom: 24px;
  }
  .mb16 {
    margin-bottom: 16px;
  }
  .mb8 {
    margin-bottom: 8px;
  }
  .container {
    gap: 32px;
  }
  .sectionContainer {
    flex: 1 1 70%;
  }
  .stickyContainer {
    gap: 32px;
    flex: 0 1 27%;
  }
  .sticky {
    position: fixed;
    top: 110px;
  }
  @media screen and (min-width: 768px) {
    .ldN {
      display: none;
    }
  }
  @media screen and (max-width: 767px) {
    padding: 0;
    .xdN {
      display: none;
    }
  }
`;

const Section = styled.section`
  &:not(:last-child) {
    margin-bottom: 16px;
  }
  &.topSection {
    margin: 0 -16px 16px;
    padding: 110px 0 0;
    .leftSection {
      flex: 0 1 50%;
      .VIPCandidate {
        max-width: 50%;
      }
    }
    .rightSection {
      gap: 32px;
    }
  }
  @media screen and (max-width: 969px) {
    &.topSection {
      .leftSection {
        .VIPCandidate {
          max-width: 100%;
        }
      }
    }
  }
  @media screen and (max-width: 767px) {
    &.topSection {
      padding: 24px 16px 0;
      .rightSection {
        gap: 12px;
      }
    }
  }
`;

const Ul = styled.ul`
  gap: 16px;
  position: relative;
  margin-left: 0;
  padding-left: 1.4em;
  li:before {
    content: '\\00ac';
    background: ${({ theme }) => theme.colors.Secondary};
    color: white;
    border-radius: 50%;
    font-size: 15px;
    width: 16px;
    height: 16px;
    transform: rotate(135deg);
    position: absolute;
    left: 0;
    text-align: center;
    line-height: 0.9;
  }
`;

const Card = styled.div`
  padding: 16px;
  &.flexContainer {
    gap: 32px;
  }
  &.stickyCard {
    transition: all 0.3s ease;
    max-width: 262px;
  }
  @media screen and (max-width: 767px) {
    &.flexContainer {
      flex-direction: column;
    }
  }
`;

const PremiumProfileBenefits = styled.ul`
  gap: 16px;
  & > li {
    flex: 0 1 30%;
  }
  @media (min-width: 768px) and (max-width: 1199px) {
  }
`;

const ResponsiveImage = styled.img`
  max-width: 100%;
  height: auto;
  &.highlightProfile {
    max-width: 177px;
    width: 100%;
  }
  @media screen and (max-width: 767px) {
    &.highlightProfile {
      max-width: 150px;
    }
  }
`;

const PayButton = styled(Button)`
  background: ${({ theme }) => theme.colors.Primary};
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.Pentanary};
`;

const DashboardMenu = styled.div`
  right: 24px;
  bottom: 24px;
  width: 69px;
  z-index: 1001;
`;

const DashboardMenuBtn = styled.button`
  background-color: #fbe710;
  padding: 0;
  width: 60px;
  height: 60px;
  line-height: 46px;
  float: right;
  transition: all, 0.3s, ease-out 0.3s;
  z-index: 1001;
  span {
    transform: translate(-50%, -50%);
  }
  .menuText {
    top: 30px;
    left: 31px;
    background-image: url('${IMAGE_CDN_DOMAIN}/assets/candidate/dashboard-icon.png');
    background-repeat: no-repeat;
    height: 30px;
    width: 30px;
  }
  &.rotate-button {
    transform: rotate(135deg);
    transition: all, 0.4s, ease-out 0.4s;
  }
`;

const DashboardMenuItems = styled.div`
  width: 220px;
  right: 7px;
  z-index: 1001;
  bottom: 68px;
  li {
    display: none;
    margin-bottom: 10px;
    &.open {
      display: list-item;
    }
    a {
      color: #000;
      transition: all 0.2s ease-in-out;
      span {
        width: 43px;
        height: 43px;
        color: ${({ theme }) => theme.colors.Primary};
        float: right;
        border: 1px solid ${({ theme }) => theme.colors.Primary};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        padding-top: 8px;
        &:hover {
          background: ${({ theme }) => theme.colors.Primary};
          color: #fff;
          svg {
            path {
              fill: white;
            }
          }
        }
      }
      svg {
        fill: ${({ theme }) => theme.colors.Primary};
        width: 23px;
      }
      .label {
        padding: 12px 56px 12px 0;
      }
    }
  }
`;

const Overlay = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  z-index: 2;
`;

const VIP_BENEFITS = [
  { title: 'Profile shown on top to recruiters', icon: 'topProfile' },
  { title: 'Apply to Unlimited Ads', icon: 'ads' },
  { title: 'New WhatsApp Job Alerts', icon: 'whatsapp' },
  { title: 'View Recruiter Contact Numbers', icon: 'contact' },
  { title: 'Track your applications', icon: 'track' }
];

const VIP_LANDING = '__vip_landing';

const VIPLanding = () => {
  const { data: { user = {} } = {}, error: errorCommon, loading: loadingCommon } = useQuery(common);
  const {
    data: { getVipPacks: { vip2Config: { price: packPrice } = {} } = {} } = {},
    error: errorPacks,
    loading: loadingPacks
  } = useQuery(getVipPacks);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false);
  const stickyRef = useRef(null);
  const footerRef = useRef(null);

  const [GetLink] = useMutation(getLink);

  useStoreUtmParams();
  const { TriggerGaEvent } = useGa();

  const utmParams = useRef();

  useEffect(() => {
    // eslint-disable-next-line no-undef
    gtag('event', 'page_view', {
      page_title: 'VIP_Landing_New',
      event_category: 'Jobs'
    });

    utmParams.current = getSessionStorage('utm_params');
  }, []);

  const fireGa = eventLabel => {
    TriggerGaEvent({
      category: 'Jobs_VIP_Landing_New',
      label: eventLabel
    });
  };

  const toggleLoginModal = () => {
    setShowLoginModal(prev => !prev);
  };

  const handleSubmit = useCallback(
    async dataOrEvent => {
      try {
        if (dataOrEvent && typeof dataOrEvent.preventDefault !== 'undefined') {
          dataOrEvent.preventDefault();
        }
        const loggedIn = Boolean(user.user);
        if (!loggedIn) {
          // store data in local storage
          await storage.set(VIP_LANDING, true);
          toggleLoginModal();
          return;
        }
        setLoading(true);
        const formData = {
          user: {
            mobile: user.user && user.user.mobile,
            email: user.user && user.user.email
          },
          type: 'VIP2',
          utmParams: utmParams.current
        };
        if (user.user && user.user.id) {
          formData.user.id = parseInt(user.user.id);
        }
        const response = await GetLink({
          variables: { payLoad: formData }
        });

        const data = response.data;
        if (data.getPaymentLink && data.getPaymentLink.paymentUrl) {
          window.location.href = data.getPaymentLink.paymentUrl;
        } else {
          setSnackbarMessage({
            type: 'error',
            message: 'Some unknown error occurred. Please try again after some time'
          });
        }
      } catch (error) {
        setSnackbarMessage({
          type: 'error',
          message: error.message || 'Some unknown error occurred. Please try again after some time'
        });
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setSnackbarMessage, GetLink, user]
  );

  useEffect(() => {
    if (loadingCommon) return;
    const verifyAndSubmitData = async () => {
      const data = await storage.get(VIP_LANDING);
      if (!data) {
        return;
      }
      handleSubmit();
    };
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const removeHash = () => {
      params.delete('action');
      let url = window.location.pathname;
      url += params.toString() ? `?${params.toString()}` : '';

      window.history.replaceState('', document.title, url);
    };
    if (user && user.user && user.user.id) {
      if (!(action && action === btoa(user.user.id))) {
        action && storage.delete(VIP_LANDING);
        removeHash();
        return;
      }
      verifyAndSubmitData();
      removeHash();
    }
  }, [loadingCommon, user, handleSubmit]);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current && footerRef.current) {
        const stickyOffset = stickyRef.current.offsetTop;
        const footerOffset = footerRef.current.offsetTop;
        const scrollTop = window.scrollY || window.pageYOffset;

        if (
          scrollTop > stickyOffset + stickyRef.current.offsetHeight &&
          scrollTop < footerOffset - stickyRef.current.offsetHeight
        ) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const loginSuccessCallback = response => {
    const { userid } = response;
    window.location.search = `?action=${btoa(userid)}`;
  };

  if (loadingCommon || errorCommon || loadingPacks || errorPacks) return null;

  return (
    <Wrapper>
      <MetaTags
        meta={{
          title: 'Buy VIP packs to get jobs faster - Quikr Jobs',
          description:
            // eslint-disable-next-line max-len
            'Find blue collar jobs at QuikrJobs. Apply to number of job vacancies, get a job relevant to your profile. Create free resume and job alerts now.'
        }}
      />
      <Section className="baW topSection">
        <FlexBox.Container>
          <Card className="dFA flexContainer">
            <div className="leftSection">
              <h1 className="h3 mb24">
                You want a Job fast, don&apos;t you? Buy a VIP pack and get a JOB now!
              </h1>
              <div className="VIPCandidate">
                <h2 className="bt1 fwM mb8">VIP Candidate</h2>
                <p className="bt2 mb8">6 benefits to get a job 10x faster</p>
                <PayButton
                  className="w100"
                  onClick={e => {
                    fireGa('Jobs_Top_Section_Pay');
                    handleSubmit(e);
                  }}
                  btnType="primary"
                  size="sm"
                  type="button"
                  loading={loading}
                  disabled={loading}
                >
                  Pay ₹{packPrice}
                </PayButton>
              </div>
            </div>
            <div className="dFA rightSection">
              <ResponsiveImage
                className="highlightProfile"
                alt="Highlight Profile"
                src={`${IMAGE_CDN_DOMAIN}/images/jobs/premiumhero.png`}
              />
              <Ul className="dF fdC">
                {VIP_BENEFITS.map((benefit, index) => (
                  <li key={index} className="bt2">
                    {benefit.title}
                  </li>
                ))}
              </Ul>
            </div>
          </Card>
        </FlexBox.Container>
      </Section>
      <FlexBox.Container className="dF container">
        <div className="sectionContainer">
          <Section>
            <Card className="baW f1">
              <h3 className="h3 fwM mb24">Benefits of buying Premium service from QuikrJobs</h3>
              <VIPTable
                price={packPrice}
                handleSubmit={e => {
                  fireGa('Jobs_VIP_Table_Pay');
                  handleSubmit(e);
                }}
                loading={loading}
              />
            </Card>
          </Section>
          <Section>
            <Card className="baW">
              <h3 className="h3 fwM mb8">Difference between Premium Profile and Free Profile</h3>
              <p className="bt2 mb16">
                Premium Profiles are always shown at the top of the recruiter search and changes are
                10 times higher to get a Job.
              </p>
              <ResponsiveImage
                alt="Profile Comparison"
                src={`${IMAGE_CDN_DOMAIN}/images/jobs/comparison.png`}
              />
            </Card>
          </Section>
          <Section>
            <Card className="baW">
              <h3 className="h3 fwM mb24">Premium Profile Benefits Details</h3>
              <PremiumProfileBenefits className="dFA jcC fwW">
                {VIP_BENEFITS.map((benefit, index) => (
                  <li key={index} className="taC">
                    <SvgComponent
                      svgSprite={vipBenefitsSprite}
                      width={40}
                      height={40}
                      id={benefit.icon}
                    />
                    <p className="bt2">{benefit.title}</p>
                  </li>
                ))}
              </PremiumProfileBenefits>
            </Card>
          </Section>
          <div ref={footerRef}></div>
        </div>
        <div className="stickyContainer xdN">
          <div className={`pR ${isSticky ? 'sticky' : ''}`} ref={stickyRef}>
            <Card className={'baW mb16 stickyCard '}>
              <h2 className="bt1 fwM mb8">VIP Candidate</h2>
              <div className="mb16">
                {VIP_BENEFITS.map((benefit, index) => (
                  <span key={index} className="bt3">
                    {index % 2 ? (
                      <b>{benefit.title}</b>
                    ) : index === 0 ? (
                      ''
                    ) : (
                      ' | ' + benefit.title + ' | '
                    )}
                  </span>
                ))}
              </div>
              <PayButton
                className="w100"
                onClick={e => {
                  fireGa('Jobs_Sticky_Section_Pay');
                  handleSubmit(e);
                }}
                btnType="primary"
                size="sm"
                type="button"
                loading={loading}
                disabled={loading}
              >
                Pay ₹{packPrice}
              </PayButton>
            </Card>
            <Card className="baW stickyCard">
              <h2 className="bt1 fwM mb8">Need Help?</h2>
              <p className="bt2 mb8">
                Call us at <Link href="tel:08067364545">0 80-67 36 45 45</Link>
              </p>
              <p className="bt2">
                Write us at{' '}
                <Link href="mailto:candidatesupport@quikr.com">candidatesupport@quikr.com</Link>
              </p>
            </Card>
          </div>
        </div>
      </FlexBox.Container>
      <DashboardMenu className="pF">
        <DashboardMenuItems className={`pA ${isDashboardMenuOpen ? 'md-show' : ''}`}>
          <ul>
            <li className={`${isDashboardMenuOpen ? 'open' : ''}`}>
              <a className="dB" href="/jobs/vip-landing">
                <span className="baW h3 br50 dB taC pR">&#9733;</span>
                <p className="label taR cP">Premium job seeker</p>
              </a>
            </li>
            <li className={`${isDashboardMenuOpen ? 'open' : ''}`}>
              <a className="dB" href="/jobs/my-profile">
                <span className="baW h3 br50 dB taC pR">
                  <AccountIcon width="23" height="23" />
                </span>
                <p className="label taR cP">My Profile</p>
              </a>
            </li>
          </ul>
        </DashboardMenuItems>
        <DashboardMenuBtn
          type="button"
          className={`dB boN pR br50 taC ${isDashboardMenuOpen ? 'rotate-button' : ''}`}
          onClick={() => setIsDashboardMenuOpen(!isDashboardMenuOpen)}
        >
          <span className={`h1 ${isDashboardMenuOpen ? '' : 'dN'}`}> + </span>
          <span className={`h1 pA menuText ${isDashboardMenuOpen ? 'dN' : ''}`} />
        </DashboardMenuBtn>
        <Overlay
          className={`pF w100 h100 ${isDashboardMenuOpen ? '' : 'dN'}`}
          onClick={() => setIsDashboardMenuOpen(!isDashboardMenuOpen)}
        />
      </DashboardMenu>
      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          setShow={toggleLoginModal}
          loginSuccessCallback={loginSuccessCallback}
        />
      )}
      {snackbarMessage.message && (
        <Snackbar
          message={snackbarMessage.message}
          type={snackbarMessage.type}
          timeout={5000}
          animationClass="Slide"
          onClose={() => setSnackbarMessage({})}
        />
      )}
    </Wrapper>
  );
};

export default VIPLanding;
